import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalNewTicket() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categoriesData, setCategoriesData] = useState({ categories: {}, languages: [] });
  const [services, setServices] = useState({ customers: [], circuits: [] });
  const [form, setForm] = useState({
    circuit_id: '',
    category: '',
    sub_category: '',
    subject: '',
    description: '',
    priority: 'medium'
  });

  const getToken = () => localStorage.getItem('portal_token');
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';

  useEffect(() => {
    if (!getToken()) {
      navigate(basePath);
      return;
    }
    fetchInitialData();
  }, [navigate]);

  const fetchInitialData = async () => {
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const [categoriesRes, servicesRes] = await Promise.all([
        fetch(`${API}/api/portal/tickets/categories`),
        fetch(`${API}/api/portal/my-services`, { headers })
      ]);

      const categoriesData = await categoriesRes.json();
      const servicesData = await servicesRes.json();

      setCategoriesData(categoriesData);
      setServices(servicesData);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.category || !form.sub_category || !form.subject || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/portal/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Ticket ${data.ticket.ticket_number} created successfully!`);
        navigate(`/portal/tickets/${data.ticket.ticket_id}`);
      } else {
        toast.error(data.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categoriesData.categories[form.category];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/dashboard`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-bold text-lg">Raise New Ticket</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Circuit Selection */}
          {services.circuits.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Select Service (Optional)</CardTitle>
                <CardDescription>Choose the affected circuit if applicable</CardDescription>
              </CardHeader>
              <CardContent>
                <Select 
                  value={form.circuit_id || "none"} 
                  onValueChange={(v) => setForm({ ...form, circuit_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select circuit (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific circuit</SelectItem>
                    {services.circuits.map((circuit) => (
                      <SelectItem key={circuit.circuit_id} value={circuit.circuit_id}>
                        {circuit.ant_circuit_id} - {circuit.location_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {/* Category Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Issue Category *</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select 
                value={form.category} 
                onValueChange={(v) => setForm({ ...form, category: v, sub_category: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoriesData.categories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedCategory && (
                <Select 
                  value={form.sub_category} 
                  onValueChange={(v) => setForm({ ...form, sub_category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCategory.sub_categories.map((sub) => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          {/* Priority */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {['low', 'medium', 'high', 'critical'].map((p) => (
                  <Button
                    key={p}
                    type="button"
                    variant={form.priority === p ? 'default' : 'outline'}
                    size="sm"
                    className={form.priority === p ? (
                      p === 'critical' ? 'bg-red-600 hover:bg-red-700' :
                      p === 'high' ? 'bg-orange-600 hover:bg-orange-700' :
                      p === 'medium' ? 'bg-blue-600 hover:bg-blue-700' :
                      'bg-slate-600 hover:bg-slate-700'
                    ) : ''}
                    onClick={() => setForm({ ...form, priority: p })}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Button>
                ))}
              </div>
              {form.priority === 'critical' && (
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Critical tickets are for complete service outages only
                </p>
              )}
            </CardContent>
          </Card>

          {/* Subject & Description */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Issue Details *</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  placeholder="Brief summary of the issue"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe your issue in detail. Include any error messages, when the issue started, and what you've already tried."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
            disabled={loading}
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Ticket...</>
            ) : (
              <><Send className="mr-2 h-4 w-4" /> Submit Ticket</>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
